import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import UsuariosAkasiaPOS from './UsuariosAkasiaPOS';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import ModalNuevoUsuarioAkasiaPOS from './Modales/ModalNuevoUsuarioAkasiaPOS';
import ModalEditarUsuarioAkasiaPOS from './Modales/ModalEditarUsuarioAkasiaPOS';
import ModalCrearSuperAdminAkasiaPOS from './Modales/ModalCrearSuperAdminAkasiaPOS';

/**
 * SeccionUsuariosAkasiaPOS
 * Gestiona la lógica de usuarios Akasia POS vinculados a una licencia
 * Siguiendo el patrón de SeccionUsuariosNubeAkasia
 */
const SeccionUsuariosAkasiaPOS = ({ idLicencia, licencias, infoSuscripcion }) => {
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showSuperAdminModal, setShowSuperAdminModal] = useState(false);
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(5);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);

    const { token } = useToken();
    const { showErrorToast } = useToast();

    /**
     * Obtiene usuarios por licencia desde el backend
     */
    const obtenerUsuarios = async (page = 1, append = false) => {
        if (!append) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        try {
            const res = await fetch(`/api/UsuariosAkasiaPOS/ObtenerUsuariosPorLicencia?idLicencia=${encodeURIComponent(idLicencia)}&pageNumber=${page}&pageSize=${pageSize}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!res.ok) throw new Error('Network response was not ok');

            const data = await res.json();
            // data expected to be ResponseType with detalles containing array or DataSet-like object
            if (data && data.iResponseType === 1 && data.detalles) {
                let detalles = data.detalles;
                try {
                    if (typeof detalles === 'string') detalles = JSON.parse(detalles);
                } catch (e) {
                    // leave as-is
                }

                let rows = [];
                let tRecords = 0;
                if (detalles) {
                    if (Array.isArray(detalles)) {
                        rows = detalles;
                    } else if (detalles.Tables && Array.isArray(detalles.Tables)) {
                        const table = detalles.Tables.find(t => t.TableName && t.TableName.toLowerCase() === 'usuarios') || detalles.Tables[0];
                        if (table) {
                            if (Array.isArray(table.Rows)) rows = table.Rows;
                            else if (Array.isArray(table)) rows = table;
                        }
                        const table2 = detalles.Tables.length > 2 ? detalles.Tables[2] : null;
                        if (table2 && Array.isArray(table2.Rows) && table2.Rows.length > 0 && table2.Rows[0].totalRecords) {
                            tRecords = parseInt(table2.Rows[0].totalRecords, 10);
                        }
                    } else if (detalles.Table && Array.isArray(detalles.Table)) {
                        rows = detalles.Table;
                    } else if (detalles.usuarios && Array.isArray(detalles.usuarios)) {
                        rows = detalles.usuarios;
                        tRecords = detalles.totalRecords || 0;
                    }
                }

                if (append) {
                    setUsuarios(prev => [...prev, ...(rows || [])]);
                } else {
                    setUsuarios(rows || []);
                    setPageNumber(1);
                }

                const totalRecordsFromResponse = tRecords || (rows || []).length;
                setTotalRecords(totalRecordsFromResponse);
                setHasMoreData((page * pageSize) < totalRecordsFromResponse);
            } else {
                if (!append) setUsuarios([]);
                showErrorToast('No se pudieron obtener los usuarios');
                setHasMoreData(false);
            }
        } catch (err) {
            showErrorToast('Error al obtener usuarios');
            if (!append) setUsuarios([]);
            setHasMoreData(false);
        } finally {
            if (!append) {
                setLoading(false);
            } else {
                setLoadingMore(false);
            }
        }
    };

    /**
     * Función para cargar más datos (infinite scroll)
     */
    const cargarMasDatos = () => {
        if (hasMoreData && !loadingMore && !loading) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            obtenerUsuarios(nextPage, true);
        }
    };

    useEffect(() => {
        if (idLicencia) obtenerUsuarios();
    }, [idLicencia]);

    /**
     * Callback cuando se crea un nuevo usuario
     */
    const onNuevoUsuarioCreado = () => {
        setShowModal(false);
        obtenerUsuarios();
    };

    /**
     * Callback cuando se actualiza un usuario
     */
    const onUsuarioActualizado = () => {
        setShowEditModal(false);
        setUsuarioSeleccionado(null);
        obtenerUsuarios();
    };

    /**
     * Callback cuando se crea el SuperAdmin
     */
    const onSuperAdminCreado = () => {
        setShowSuperAdminModal(false);
        obtenerUsuarios();
    };

    /**
     * Abre el modal de edición para un usuario
     */
    const handleEditarUsuario = (usuario) => {
        setUsuarioSeleccionado(usuario);
        setShowEditModal(true);
    };

    return (
        <>
            <UsuariosAkasiaPOS 
                usuarios={usuarios}
                loading={loading}
                loadingMore={loadingMore}
                hasMoreData={hasMoreData}
                cargarMasDatos={cargarMasDatos}
                totalRecords={totalRecords}
                licencias={licencias}
                onNuevoUsuario={() => setShowModal(true)}
                onEditarUsuario={handleEditarUsuario}
                onCrearSuperAdmin={() => setShowSuperAdminModal(true)}
            />

            <ModalNuevoUsuarioAkasiaPOS
                show={showModal}
                onHide={() => setShowModal(false)}
                idLicencia={idLicencia}
                licencias={licencias}
                onCreated={onNuevoUsuarioCreado}
                infoSuscripcionProp={infoSuscripcion}
            />

            <ModalEditarUsuarioAkasiaPOS
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setUsuarioSeleccionado(null);
                }}
                usuario={usuarioSeleccionado}
                onUpdated={onUsuarioActualizado}
            />

            <ModalCrearSuperAdminAkasiaPOS
                show={showSuperAdminModal}
                onHide={() => setShowSuperAdminModal(false)}
                idLicencia={parseInt(idLicencia)}
                onCreated={onSuperAdminCreado}
            />
        </>
    );
};

SeccionUsuariosAkasiaPOS.propTypes = {
    idLicencia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    licencias: PropTypes.shape({
        bIsActive: PropTypes.bool,
        bIsPeriodoPrueba: PropTypes.bool
    }),
    infoSuscripcion: PropTypes.object
};

export default SeccionUsuariosAkasiaPOS;
